import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  CreditCard,
  ExternalLink,
  FileSignature,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface Props {
  customerName: string;
  customerPhone: string;
  address: string;
  squares: number;
  pricePerSq: number;
  wastePercent: number;
  complexityPercent: number;
  roofTotal: number;
  tradesSubtotal: number;
  commissionAmount: number;
  finalPrice: number;
  tradeCount: number;
  onClose: () => void;
}

export function StepRbFinish({
  customerName,
  customerPhone,
  address,
  squares,
  pricePerSq,
  wastePercent,
  complexityPercent,
  roofTotal,
  tradesSubtotal,
  commissionAmount,
  finalPrice,
  tradeCount,
  onClose,
}: Props) {
  const navigate = useNavigate();
  const { orgId, user } = useAuth();
  const [partnerUrl, setPartnerUrl] = useState<string | null>(null);
  const [appScheme, setAppScheme] = useState<string>("goodleap://");
  const [creatingJob, setCreatingJob] = useState(false);
  const [financingOpened, setFinancingOpened] = useState(false);

  const customerPrice = finalPrice;

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const { data } = await supabase
        .from("organizations")
        .select("goodleap_partner_url, goodleap_app_scheme")
        .eq("id", orgId)
        .single();
      if (data) {
        setPartnerUrl(data.goodleap_partner_url || null);
        setAppScheme(data.goodleap_app_scheme || "goodleap://");
      }
    })();
  }, [orgId]);

  const launchGoodLeap = () => {
    if (!partnerUrl) {
      toast.error("GoodLeap not configured for this org");
      return;
    }
    setFinancingOpened(true);
    const start = Date.now();
    window.location.href = appScheme;
    setTimeout(() => {
      if (Date.now() - start < 1600) {
        window.open(partnerUrl, "_blank", "noopener,noreferrer");
      }
    }, 800);
  };

  const handleGenerateAgreement = async () => {
    if (!orgId || !user) {
      toast.error("Login required");
      return;
    }
    setCreatingJob(true);
    try {
      // Parse address into parts (best-effort)
      const parts = address.split(",").map((p) => p.trim());
      const street = parts[0] || address;
      const city = parts[1] || "";
      const stateZip = (parts[2] || "").split(" ").filter(Boolean);
      const stateAbbr = stateZip[0] || "";
      const zip = stateZip[1] || "";

      const { data: job, error } = await supabase
        .from("jobs")
        .insert({
          org_id: orgId,
          status: "new",
          property_address: street,
          property_city: city,
          property_state: stateAbbr,
          property_zip: zip,
          owner_name: customerName,
          owner_phone: customerPhone || null,
          estimated_value: customerPrice,
          notes: `Created via mobile Instant Quote. ${squares.toFixed(
            1
          )} sq @ $${pricePerSq}/sq · waste ${wastePercent}% · complexity +${complexityPercent}% · add-ons $${tradesSubtotal.toLocaleString()} · markup $${commissionAmount.toLocaleString()} = $${customerPrice.toLocaleString()}`,
          created_by: user.id,
        })
        .select("id")
        .single();

      if (error) throw error;
      toast.success("Job created — opening service agreement");
      navigate(`/service-agreement/${job.id}`);
      onClose();
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "Failed to create job");
    } finally {
      setCreatingJob(false);
    }
  };

  return (
    <div className="px-4 pt-6 pb-8 sm:px-5 space-y-6 min-w-0 overflow-x-hidden">
      <div>
        <div className="text-xs font-semibold text-primary uppercase tracking-wider mb-1.5">
          Step 4 / 4
        </div>
        <h2 className="text-2xl font-bold tracking-tight">Financing & Sign</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Run the customer through GoodLeap, then generate the service agreement.
        </p>
      </div>

      {/* Summary */}
      <div className="rounded-2xl border border-border/60 bg-card p-4 space-y-3 min-w-0">
        <div className="min-w-0">
          <div className="text-sm font-semibold">{customerName}</div>
          <div className="mt-1 text-xs text-muted-foreground break-words">{address}</div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2.5">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Quote Total</div>
            <div className="mt-1 text-2xl font-extrabold text-primary tabular-nums">${customerPrice.toLocaleString()}</div>
          </div>
          <div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2.5">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Measured Roof</div>
            <div className="mt-1 text-2xl font-extrabold tabular-nums">{squares.toFixed(1)} sq</div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <div className="rounded-lg border border-border/60 bg-background px-2 py-2 text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Roof</div>
            <div className="mt-1 text-sm font-bold tabular-nums">${roofTotal.toLocaleString()}</div>
          </div>
          <div className="rounded-lg border border-border/60 bg-background px-2 py-2 text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Products</div>
            <div className="mt-1 text-sm font-bold tabular-nums">${tradesSubtotal.toLocaleString()}</div>
          </div>
          <div className="rounded-lg border border-border/60 bg-background px-2 py-2 text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Markup</div>
            <div className="mt-1 text-sm font-bold tabular-nums">${commissionAmount.toLocaleString()}</div>
          </div>
        </div>
        <div className="flex flex-col gap-2 pt-1 border-t border-border/60 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <span className="text-xs text-muted-foreground leading-5 break-words">
            ${pricePerSq}/sq · waste {wastePercent}% · complexity +{complexityPercent}%
          </span>
          <span className="text-xs font-semibold text-muted-foreground sm:text-right">
            {tradeCount > 0 ? `${tradeCount} added option${tradeCount === 1 ? "" : "s"}` : "Roof only"}
          </span>
        </div>
      </div>

      {/* Step 1 — GoodLeap */}
      <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
            1
          </div>
          <h3 className="font-bold text-sm">Check Financing Approval</h3>
          {financingOpened && (
            <CheckCircle2 className="h-4 w-4 text-success ml-auto" />
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Opens GoodLeap so the homeowner can apply and see what they're approved for.
        </p>
        <Button
          onClick={launchGoodLeap}
          disabled={!partnerUrl}
          className="w-full h-12 gap-2 font-semibold"
          size="lg"
          variant={financingOpened ? "outline" : "default"}
        >
          <CreditCard className="h-4 w-4" />
          {financingOpened ? "Re-open GoodLeap" : "Open GoodLeap"}
          <ExternalLink className="h-3 w-3 opacity-60" />
        </Button>
        {!partnerUrl && (
          <p className="text-[11px] text-destructive">
            GoodLeap partner URL not configured for your org.
          </p>
        )}
      </div>

      {/* Step 2 — Service Agreement */}
      <div className="rounded-2xl border-2 border-border/60 bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-bold">
            2
          </div>
          <h3 className="font-bold text-sm">Generate Service Agreement</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          Creates the job, prefills the RB Restoration agreement, and opens it for
          customer signature.
        </p>
        <Button
          onClick={handleGenerateAgreement}
          disabled={creatingJob}
          className="w-full h-12 gap-2 font-semibold"
          size="lg"
        >
          {creatingJob ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileSignature className="h-4 w-4" />
          )}
          Generate & Sign Agreement
        </Button>
      </div>
    </div>
  );
}
